import React from 'react'
import ReactGA from 'react-ga'
import { withRouter } from 'react-router'
import { Formik, Form, Field } from 'formik'
import PropTypes from 'prop-types'
import ReactRouterPropTypes from 'react-router-prop-types'
import * as Yup from 'yup'

import ButtonSpacer from '../../ButtonSpacer/ButtonSpacer'
import { dataByTypename } from '../../../utils/apollo-helpers'
import { Notification } from '../../Notification/Notification'
import { Login, useAuth } from '../../AuthProvider/AuthProvider'
import { PoweredByToastModal } from '../../PoweredByToastModal/PoweredByToastModal'
import Input from '../../Form/Input/Input'
import { Password } from '../../Form/Password/Password'
import Button from '../../Button/Button'
import { ModeLink, ModeRedirect } from '../../ModeRouter/ModeRouter'
import { emailSchema, passwordSchema } from '../../../utils/form-schemas'

import './LoginModal.css'

const LoginModal = ({ onClose, location: { state = {} } }) => {
  const { setMfaState, mfaRequiresVerification } = useAuth()

  let notificationText
  if (state.createAccountRedirect) {
    notificationText = 'Looks like you already have an account! Enter your password to complete checkout or continue as guest.'
  }
  if (state.verifyAccountRedirect) {
    notificationText = 'It looks like you have already verified your account! Log in below.'
  }

  if (mfaRequiresVerification) {
    return (
      <ModeRedirect
        mode='mfa'
      />
    )
  }

  return (
    <PoweredByToastModal
      onClose={onClose}
      header={
        <React.Fragment>
          <h2 data-testid='login-modal-header' styleName='header'>Log In</h2>
          {!notificationText && <p data-testid='login-modal-description' styleName='description'>
            See past orders, get your favorites again and save all your info in
            one place.
          </p>}
        </React.Fragment>
      }
    >
      <Login>
        {(login, {loading, error}) => (
          <Formik
            initialValues={{ email: state.email || '', password: '' }}
            validationSchema={Yup.object().shape({
              email: emailSchema,
              password: passwordSchema
            })}
            onSubmit={async values => {
              const input = { variables: { input: values } }
              const result = await login(input)

              const {
                MfaChallengeGeneratedResponse,
                LoginError
              } = dataByTypename(result.data.login)

              if (MfaChallengeGeneratedResponse) {
                setMfaState({
                  mfaRequiresVerification: true,
                  email: values.email,
                  ...result.data.login
                })
              } else if (!LoginError) {
                ReactGA.event({
                  category: 'User Account',
                  action: 'Login Success'
                })

                onClose()
              }
            }}
          >
            {({ isValid }) => (
              <Form>
                {notificationText && <Notification>
                  {notificationText}
                </Notification>}
                {error && (
                  <Notification severity='error'>
                    Invalid username or password combination.
                  </Notification>
                )}
                <Field
                  data-testid='login-modal-email'
                  autoFocus
                  id='login-email'
                  name='email'
                  type='text'
                  label='email'
                  component={Input}
                />
                <Field
                  data-testid='login-modal-password'
                  id='login-password'
                  name='password'
                  label='password'
                  component={Password}
                />
                <Button
                  data-testid='login-modal-button-submit'
                  type='submit'
                  style='primary'
                  responsive
                  loading={loading}
                  disabled={!isValid}
                >
                  Log In
                </Button>
                <ButtonSpacer vertical />
                <ModeLink data-testid='login-modal-button-forgot-password' style='secondaryWide' mode='forgot'>
                  Forgot Password
                </ModeLink>
              </Form>
            )}
          </Formik>
        )}
      </Login>
    </PoweredByToastModal>
  )
}

LoginModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  location: ReactRouterPropTypes.location
}

export default withRouter(LoginModal)
